import type { TranscriptSegment } from './types'

export interface TranscriptPart { text: string; start?: number; end?: number }
const normalized = (text: string) => text.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')

/** Keep the premium text verbatim; never invent timestamps for missing words. */
export function timedTranscriptParts(segment: TranscriptSegment): TranscriptPart[] {
  const words = segment.words || []
  const plain = [{ text: segment.text }]
  if (!words.length) return plain
  const partial = segment.alignment_status === 'needs_alignment'
  // Reconciliation retains uniquely matched measured words while flagging the
  // whole utterance as unsafe for automatic editing. Display those observations
  // only for local text disagreements; cross-request/speaker conflicts stay plain.
  if (partial && (!segment.timing_issues?.length || segment.timing_issues.some(issue =>
    !['omission_or_substitution', 'ambiguous_match', 'unmatched_whisper_word'].includes(issue.kind))
    || words.some(word => word.timing_model !== 'whisper-1'))) return plain
  if (words.some((word, index) => !Number.isFinite(word.start) || !Number.isFinite(word.end)
    || word.start < 0 || word.end <= word.start || word.start < segment.start - .35
    || word.end > segment.end + .35 || (partial && (word.start < segment.start || word.end > segment.end))
    || (index > 0 && word.start < words[index - 1].end - .02))) return plain
  if (normalized(segment.text) !== words.map((word) => normalized(word.w)).join('')) {
    if (!partial) return plain
    return partialTranscriptParts(segment)
  }
  const tokens = [...segment.text.matchAll(/\S+/gu)]
  const parts: TranscriptPart[] = []
  let cursor = 0, tokenIndex = 0
  for (const word of words) {
    const target = normalized(word.w)
    if (!target || !tokens[tokenIndex]) return plain
    const start = tokens[tokenIndex].index!
    let text = '', end = start
    while (tokenIndex < tokens.length && normalized(text).length < target.length) {
      const token = tokens[tokenIndex++]
      end = token.index! + token[0].length
      text = segment.text.slice(start, end)
    }
    if (normalized(text) !== target) return plain
    if (start > cursor) parts.push({ text: segment.text.slice(cursor, start) })
    parts.push({ text, start: word.start, end: word.end })
    cursor = end
  }
  if (cursor < segment.text.length) parts.push({ text: segment.text.slice(cursor) })
  return parts
}

function partialTranscriptParts(segment: TranscriptSegment): TranscriptPart[] {
  const tokens = [...segment.text.matchAll(/\S+/gu)]
  const matches: { from: number; to: number; start: number; end: number }[] = []
  for (const word of segment.words || []) {
    if (word.start < segment.start || word.end > segment.end) continue
    const target = normalized(word.w)
    if (!target) continue
    const candidates: { from: number; to: number }[] = []
    for (let i = 0; i < tokens.length; i++) {
      let combined = ''
      for (let j = i; j < tokens.length && combined.length < target.length; j++) {
        combined += normalized(tokens[j][0])
        if (combined === target) candidates.push({ from: tokens[i].index!, to: tokens[j].index! + tokens[j][0].length })
      }
    }
    // Repeated words cannot be assigned to an arbitrary occurrence. Missing
    // words remain verbatim and untimed rather than receiving interpolated times.
    if (candidates.length === 1) matches.push({ ...candidates[0], start: word.start, end: word.end })
  }
  const safe = matches.filter((match) => !matches.some(other => other !== match && match.from < other.to && other.from < match.to))
  if (safe.some((match, index) => index > 0 && match.from < safe[index - 1].to)) return [{ text: segment.text }]
  const parts: TranscriptPart[] = []
  let cursor = 0
  for (const match of safe) {
    if (match.from > cursor) parts.push({ text: segment.text.slice(cursor, match.from) })
    parts.push({ text: segment.text.slice(match.from, match.to), start: match.start, end: match.end })
    cursor = match.to
  }
  if (cursor < segment.text.length) parts.push({ text: segment.text.slice(cursor) })
  return parts.length ? parts : [{ text: segment.text }]
}

export function transcriptSpeaker(segment: TranscriptSegment) {
  const id = segment.speaker || ''
  const chunk = /^chunk-(\d+):(.+)$/.exec(id)
  const raw = segment.speaker_label || chunk?.[2] || id
  const anonymous = /^(?:[A-Z]|\d+|speaker[_ -]?\d+)$/i.test(raw)
  return {
    label: segment.speaker_name || (!raw ? 'Speaker unknown' : anonymous ? `Speaker ${raw.replace(/^speaker[_ -]?/i, '')}` : raw),
    section: chunk ? Number(chunk[1]) + 1 : null,
    description: chunk ? `Speaker label within section ${Number(chunk[1]) + 1}. Identity across sections has not been verified.` : undefined,
  }
}

/** Called directly from a user gesture so playback starts as well as seeking. */
export function seekAndPlay(video: HTMLVideoElement | null, seconds: number): boolean {
  if (!video || !Number.isFinite(seconds) || seconds < 0 || video.readyState < 1) return false
  const duration = Number.isFinite(video.duration) ? video.duration : Infinity
  video.currentTime = Math.min(seconds, Math.max(0, duration - .001))
  void video.play().catch(() => { /* Native player controls remain available if playback is blocked. */ })
  return true
}
